import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icones from 'lucide-react';
import { construireContexte } from '../../../utils/contexteGabarit';
import { rendre } from '../../../utils/gabarit';

/**
 * « Va bien avec » — la suggestion d'un module complémentaire.
 *
 * Le bloc ne porte qu'un CODE de module ; le nom, le résumé, l'icône et la
 * couleur sont lus en base. Recopier ces valeurs dans 21 pages × 8 langues
 * les figerait le jour où un module est renommé ou retraduit.
 *
 * La suggestion elle-même reste éditoriale : c'est un choix humain de dire
 * que Quartier va avec Carte. Le système ne le devine pas — il se contente
 * de rendre exact ce que l'auteur a choisi.
 *
 * Module introuvable ou désactivé : le bloc ne rend RIEN. Une carte vide
 * pointant vers une page morte est pire que pas de suggestion.
 */

const GABARIT_TEXTE_DEFAUT = '{{ m.resume }}';

function IconeModule({ nom, couleur }) {
    if (!nom) return null;
    const Composant = Icones[nom];
    if (typeof Composant !== 'function') return null;

    const style = {};
    if (couleur) style.color = couleur;

    return <Composant className="w-6 h-6" style={style} aria-hidden="true" />;
}

const ModuleLieBloc = ({ bloc, langue = 'fr' }) => {
    const [module, setModule] = useState(null);
    const [contexte, setContexte] = useState(null);
    const [chargement, setChargement] = useState(true);

    const code = String(bloc?.codeModule || bloc?.codemodule || '').trim();

    useEffect(() => {
        if (!code) {
            setChargement(false);
            return;
        }

        let annule = false;

        construireContexte(langue)
            .then((c) => {
                if (annule) return;
                setContexte(c);
                setModule(c.module[code] || null);
            })
            .catch(() => {
                if (!annule) setModule(null);
            })
            .finally(() => {
                if (!annule) setChargement(false);
            });

        return () => { annule = true; };
    }, [code, langue]);

    if (chargement) return null;
    if (!module || !module.lien) return null;

    const gabaritTexte = bloc?.gabarit?.texte || GABARIT_TEXTE_DEFAUT;
    const texte = rendre(gabaritTexte, { ...contexte, m: module }, langue);

    // Le libellé d'accroche est du contenu éditorial : il vient du bloc,
    // dans la langue de la page, et n'a rien à faire en dur ici.
    const accroche = bloc?.accroche || '';
    const lienTexte = bloc?.lienTexte || '';

    return (
        <section className="mb-10" data-testid="bloc-moduleLie">
            <Link
                to={module.lien}
                className="flex items-center gap-4 bg-white border border-purple-200 rounded-2xl px-6 py-5 hover:border-purple-300 hover:shadow-sm transition-all"
            >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                    <IconeModule nom={module.icone} couleur={module.couleur} />
                </div>

                <div className="flex-grow min-w-0">
                    {accroche && (
                        <div className="text-xs font-semibold uppercase tracking-wider text-cabaza-purple mb-0.5">
                            {accroche}
                        </div>
                    )}
                    <div className="font-bold text-gray-900">{module.nom}</div>
                    {texte && (
                        <div className="text-sm text-gray-600 mt-0.5">{texte}</div>
                    )}
                </div>

                {lienTexte && (
                    <div className="flex-shrink-0 text-sm font-semibold text-cabaza-purple">
                        {lienTexte}
                    </div>
                )}
            </Link>
        </section>
    );
};

export default ModuleLieBloc;
